import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import * as failedDocsClient from './api/failed-documents-client';
import type { FailedDocument } from './api/failed-documents-client';
import styles from './failed-documents.module.css';

export function FailedDocumentsPage() {
  const [docs, setDocs] = useState<FailedDocument[] | null>(null);
  const [retrying, setRetrying] = useState<Set<string>>(new Set());
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  async function load() {
    try {
      const res = await failedDocsClient.list();
      setDocs(res.documents);
      setErrorMessage(null);
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : 'Failed to load failed documents');
      setDocs([]);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function handleRetry(documentId: string) {
    setRetrying((prev) => new Set(prev).add(documentId));
    try {
      await failedDocsClient.retry(documentId);
      // Optimistically drop the row from the list — the retry resets state
      // to scan/pending and re-enqueues, so the doc will no longer match
      // status='error' on the next refresh anyway.
      setDocs((prev) => (prev ?? []).filter((d) => d.id !== documentId));
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : `Retry failed for ${documentId}`);
    } finally {
      setRetrying((prev) => {
        const next = new Set(prev);
        next.delete(documentId);
        return next;
      });
    }
  }

  if (docs === null) {
    return (
      <div className={styles.page}>
        <h1 className={styles.pageTitle}>Failed Documents</h1>
        <p>Loading…</p>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <h1 className={styles.pageTitle}>Failed Documents</h1>
      <p className={styles.subhead}>
        Documents stranded at <code>status=error</code> from upstream pipeline failures (image-process, OCR, or export-enqueue). Retry resets each document to <code>scan/pending</code> and re-enqueues the OCR flow.
      </p>

      {errorMessage && (
        <div className={styles.errorBanner} role="alert">
          {errorMessage}
        </div>
      )}

      {docs.length === 0 ? (
        <div className={styles.emptyState}>
          <p>No failed documents.</p>
        </div>
      ) : (
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Filename</th>
              <th>Batch</th>
              <th>Step</th>
              <th>Hold Reason</th>
              <th>Failed At</th>
              <th aria-label="actions" />
            </tr>
          </thead>
          <tbody>
            {docs.map((doc) => (
              <tr key={doc.id}>
                <td className={styles.filename}>{doc.filename}</td>
                <td>
                  {/* E-050 — lead with the batch identity; raw name in the
                      tooltip when it adds information. */}
                  <Link
                    to={`/batches/${doc.batchId}`}
                    title={doc.batchLabel && doc.batchLabel !== doc.batchName ? doc.batchName : undefined}
                  >
                    {doc.batchLabel ?? doc.batchName}
                  </Link>
                </td>
                <td>{doc.currentStep}</td>
                <td className={styles.holdReason}>{doc.holdReason ?? '—'}</td>
                <td className={styles.timestamp}>{new Date(doc.updatedAt).toLocaleString()}</td>
                <td>
                  <button
                    type="button"
                    className={styles.retryBtn}
                    onClick={() => handleRetry(doc.id)}
                    disabled={retrying.has(doc.id)}
                  >
                    {retrying.has(doc.id) ? 'Retrying…' : 'Retry'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
